import React, { useEffect, useState } from "react";
import {
  UncontrolledDropdown,
  Button,
  Row,
  Col,
  Spinner,
  Alert,
} from "reactstrap";
import { Modal } from "antd";
import { ReactTyped } from "react-typed";
import { FormProvider, useForm } from "react-hook-form";
import workExecutionApis from "../../../api/WorkOrderManagement/WorkExecutionApis";
import { Icon } from "../../../components/Component";
import FormInput from "../../form-components/InputComponent";

const blobUrl: String = process.env.REACT_APP_BLOB_URL || "";
const blobContainer: String = process.env.REACT_APP_BLOB_CONTAINER || "";

const WorkOrderLineBulkUpload = ({
  onUpload,
  workCategoryOptions,
  uomOptions,
  taxOptions,
  budgetHeaderId,
  workLevelWatch,
  enableTaxWatch,
  taxApplicationLevelWatch,
  siteId,
  workType,
  existingLines = [],
  disabled = false,
}: {
  onUpload: (workOrderLines: any[]) => void;
  workCategoryOptions: any[];
  uomOptions: any[];
  taxOptions: any[];
  budgetHeaderId?: any;
  workLevelWatch?: any;
  taxApplicationLevelWatch?: any;
  enableTaxWatch?: any;
  existingLines?: any[];
  siteId?: any;
  workType?: any;
  disabled?: boolean;
}) => {
  const [loading, setLoading] = useState(false);
  const [blockEdit, setBlockEdit] = useState(false);
  const [response, setResponse] = useState({
    status: "",
    message: "",
  });
  const [showModal, setShowModal] = useState(false);

  const methods = useForm({
    mode: "onChange",
  });

  const {
    handleSubmit,
    watch,
    formState: { errors },
    setValue,
  } = methods;

  const files = watch("attachment");

  useEffect(() => {
    setResponse({ status: "", message: "" });
  }, [files]);

  const handleUpload = async () => {
    try {
      const attachment = files;
      if (!attachment || !attachment[0]) {
        setResponse({
          status: "error",
          message: "Please select a file",
        });
        setBlockEdit(false);
        setLoading(false);
        return;
      }
      uploadFile(attachment, setResponse, "File Uploaded Successfully");
    } catch (error) {
      console.log("Upload error:", error);
    }
  };

  const uploadFile = async (
    attachment: any,
    setResponse: any,
    successResponse: any
  ) => {
    try {
      const _file = attachment?.[0];
      const formData: any = new FormData();
      formData.append("files", _file);

      // Map existing lines to the format expected by the API - only send taskTitle and workCategoryName
      const mappedExistingLines = existingLines.map((line: any) => ({
        taskTitle: line.taskTitle || "",
        workCategoryName: line.workCategoryName || line.workCategory || "",
        weightagePct: line.weightagePercentage || 0,
      }));

      // Create request payload with existing lines
      const request = {
        lines: mappedExistingLines,
        budgetId: budgetHeaderId,
        segmentLevel: workLevelWatch && workLevelWatch === "Tower / Block" ? "Tower" : workLevelWatch,
        workType: workType,
        siteId: siteId,
        enableTax: enableTaxWatch,
        taxApplicationLevel: taxApplicationLevelWatch || null,
      };

      formData.append("body", JSON.stringify(request));

      // Call the API for Work Order Line bulk upload
      const res: any = await workExecutionApis.bulkUploadValidateWorkOrderLines(
        formData
      );

      console.log("Bulk upload API response:", res);

      const { status, error, message, data } = res || {};

      if (status) {
        setResponse({
          status: "success",
          message: successResponse,
        });

        // Process the uploaded Work Order Lines
        if (data && data.lines) {
          console.log("Processing uploaded lines:", data.lines);

          const processedWorkOrderLines = data.lines.map(
            (item: any, index: number) => {
              // Calculate line amount and tax
              const lineAmount = (item.quantity || 0) * (item.rate || 0);
              const taxAmount = item.taxRate
                ? (lineAmount * item.taxRate) / 100
                : 0;
              const totalAmount = lineAmount + taxAmount;

              // Process stages if available
              const stages =
                item.stages?.map((stage: any, stageIndex: number) => ({
                  key: `stage_${index}_${stageIndex}_${Date.now()}`,
                  stageTitle: stage.stageTitle || "",
                  splitPercentage: stage.splitPercentage || 0,
                })) || [];

              return {
                key: `bulk_${index}_${Date.now()}`,
                id: `bulk_${index}_${Date.now()}`,
                lineNumber: (index + 1).toString(),
                workCategory: item.workCategoryId?.toString() || "",
                workCategoryName: item.workCategoryName || "",
                subCategory: item.subCategory || "",
                taskTitle: item.taskTitle || "",
                taskDescription: item.description || "",
                quantity: item.quantity || 0,
                uom: item.uomId?.toString() || "",
                uomName: item.uomName || "",
                weightagePercentage: item.weightagePct || 0,
                rate: item.rate || 0,
                taxType: item.taxId?.toString() || "",
                taxTypeName: item.taxName || "",
                taxPercentage: item.taxRate || 0,
                taxAmount: taxAmount,
                totalAmount: totalAmount,
                source: "New Work Line",
                sourceId: "",
                sectionId: "",
                executedQuantity: 0,
                remainingQuantity: item.quantity || 0,
                completionPercentage: 0,
                budgetLineId: item.budgetLineId || 0,
                budgetLineName: item.budgetLineName || "",
                budgetAllocation: item.budgetAllocation || 0,
                sgstRate: item.sgstRate || 0,
                cgstRate: item.cgstRate || 0,
                igstRate: item.igstRate || 0,
                errorFlag: item.errorFlag,
                errorMessage: item.errorMessage,
                towerVilla: item.towerVilla || null,
                towerVillaId: item.siteStructureUnitId || null,
                floorName: item.floorNo || null,
                flatName: item.flatName || null,
                flatId : item.siteStructureSubUnitId || null,
                locationId: item.locationId || null,
                locationName: item.locationName || null,
                // Stage-wise breakdown
                hasStages: item.hasStages || stages.length > 0,
                stages: stages,
                // Work type from parent component
                workType: workType,
              };
            }
          );

          console.log("Processed work order lines:", processedWorkOrderLines);

          // Call the parent component's onUpload function
          onUpload(processedWorkOrderLines);

          // Close modal immediately after upload
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }
        return;
      }

      setResponse({
        status: "error",
        message: error + ": " + message,
      });
      setBlockEdit(false);
      setLoading(false);
    } catch (error) {
      console.log("Upload file error:", error);
      setResponse({
        status: "error",
        message: "An error occurred while uploading the file",
      });
      setBlockEdit(false);
      setLoading(false);
    }
  };

  return (
    <>
      <div
        onClick={() => {
          if (!disabled) {
            setShowModal(true);
          }
        }}
        className="ant-btn ant-btn-default w-100"
        style={{
          height: "38px",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "auto",
        }}
      >
        <Icon name="upload-cloud" style={{ marginRight: "8px" }}></Icon>
        <span>Bulk Upload Work Lines</span>
      </div>

      <Modal
        title="Bulk Work Order Line Upload"
        open={showModal}
        footer={null}
        onCancel={() => {
          setShowModal(false);
          setBlockEdit(false);
          setValue("attachment", null);
          setLoading(false);
          setResponse(() => ({
            status: "",
            message: "",
          }));
        }}
        width={"60%"}
      >
        <FormProvider {...methods}>
          <form>
            <p>
              You're about to upload multiple work order lines at once. To
              ensure a smooth import, please{" "}
              <a
                href={`${blobUrl}${blobContainer}/WorkOrderLinesUpload.xlsx`}
                target="_self"
                style={{
                  textDecoration: "underline",
                }}
              >
                Download Template
              </a>{" "}
              and fill in the required work order line data accurately and
              completely, then upload the file below.
            </p>

            <div className="text-right mt-3"></div>
            <p>
              <strong>Note : </strong>
              All fields in the template are mandatory. Each record must have
              unique information to avoid errors during the import process.
            </p>
            <Col md={4}>
              {files && files[0]?.name && (
                <div className="form-group m-0">
                  <div className="form-label-wrapper">
                    <label className="form-label" htmlFor={"attachment"}>
                      File Upload Area
                    </label>
                  </div>
                  <div
                    className="form-control-wrap d-flex align-items-center justify-content-between border p-1"
                    style={{ borderRadius: "3px" }}
                  >
                    <span>{files && files[0]?.name}</span>
                    {!blockEdit && (
                      <span
                        onClick={() => {
                          setValue("attachment", null);
                        }}
                        style={{
                          cursor: "pointer",
                          marginLeft: "10px",
                          display: "block",
                          fontSize: 14,
                        }}
                      >
                        <Icon name="cross"></Icon> &nbsp;
                      </span>
                    )}
                  </div>
                </div>
              )}
              <div
                style={{
                  display: `${files && files[0]?.name ? "none" : "block"}`,
                }}
              >
                <FormInput
                  type="file"
                  name="attachment"
                  label="File Upload Area"
                  placeholder="File Upload Area"
                  accept=".xlsx"
                />
              </div>
            </Col>

            <Alert
              isOpen={response?.status === "error"}
              color="danger"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <ReactTyped
                strings={[response?.message]}
                typeSpeed={30}
                showCursor={false}
              />
            </Alert>

            <Alert
              isOpen={response?.status === "success"}
              color="success"
              className="start-0 w-100 py-1 mt-2"
              style={{
                top: 0,
                textAlign: "left",
                fontSize: "14px",
                cursor: "default",
              }}
            >
              <p>
                {response?.message}, You can{" "}
                <span
                  className="inlineTableCellLink"
                  onClick={() => {
                    setShowModal(false);
                    setBlockEdit(false);
                    setValue("attachment", null);
                    setLoading(false);
                    setResponse(() => ({
                      status: "",
                      message: "",
                    }));
                  }}
                >
                  close
                </span>{" "}
                this popup
              </p>
            </Alert>

            <Row className="mt-4">
              <Col>
                {response?.status == "success" ? (
                  <></>
                ) : (
                  <ul className="actions clearfix d-flex">
                    <li>
                      {loading ? (
                        <Button color="primary" type="button" size="md">
                          <Spinner size="sm" color="light" />
                        </Button>
                      ) : (
                        <Button
                          color="primary"
                          type="button"
                          disabled={!files?.[0]}
                          onClick={() => {
                            setResponse(() => ({
                              status: "",
                              message: "",
                            }));
                            setBlockEdit(true);
                            setLoading(true);
                            handleUpload();
                          }}
                        >
                          Upload
                        </Button>
                      )}
                    </li>
                    {!loading && (
                      <li className="m-3 mt-0">
                        <Button
                          color="primary"
                          onClick={() => {
                            setShowModal(false);
                            setValue("attachment", null);
                          }}
                          className="btn-dim btn btn-secondary"
                        >
                          Cancel
                        </Button>
                      </li>
                    )}
                  </ul>
                )}
              </Col>
            </Row>
          </form>
        </FormProvider>
      </Modal>
    </>
  );
};

export default WorkOrderLineBulkUpload;
